/**
 * PATH: apps/qa/src/pages/new-inspection/useNewJobDraft.ts
 * FILE_ID: QA-NEWINSPECTION-JOB-DRAFT-HOOK-001
 * PURPOSE: Stage title/client/report/participants and create a draft job.
 */
import { useCallback, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { QaParticipantPerson, QaParticipantRow } from '@quotech/job-hub/jobs/qa'
import { api } from '@/lib/api'
import type { Job, ToastNotification } from './inspection-types'

export type ClientHit = { id: number; client_name: string }

type CreatedJobPayload = {
  id: number
  display_name?: string | null
  client_name?: string | null
  site_address?: string | null
  status?: string | null
}

function rowsOf(data: unknown): Array<Record<string, unknown>> {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>
  const results = (data as { results?: unknown[] } | null)?.results
  return Array.isArray(results) ? (results as Array<Record<string, unknown>>) : []
}

export function fallbackJobTitle(): string {
  return `New job ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`
}

export function useNewJobDraft({
  organisationId,
  onClose,
  onCreated,
  showToast,
}: {
  organisationId: string | number
  onClose: () => void
  onCreated: (job: Job) => void
  showToast: (toast: Omit<ToastNotification, 'id'>) => void
}) {
  const [title, setTitle] = useState('QA Job - ')
  const titleReady = true
  const [clientId, setClientId] = useState<number | null>(null)
  const [clientLabel, setClientLabel] = useState('')
  const [reportTitle, setReportTitle] = useState('')
  const [staged, setStaged] = useState<QaParticipantRow[]>([])
  const [error, setError] = useState<string | null>(null)

  const searchClients = useCallback(async (q: string): Promise<ClientHit[]> => {
    const { data } = await api().get('/report/clients/', {
      params: { search: q, organisation_id: organisationId, page_size: 20 },
    })
    return rowsOf(data)
      .map((row) => ({
        id: Number(row.id),
        client_name: typeof row.client_name === 'string' ? row.client_name : '',
      }))
      .filter((row) => Number.isFinite(row.id) && row.client_name)
  }, [organisationId])

  const createClient = useCallback(async (name: string): Promise<ClientHit | null> => {
    const { data } = await api().post<Record<string, unknown>>(
      '/report/clients/',
      { client_name: name },
      { params: { tenant: organisationId } },
    )
    const id = Number(data?.id)
    if (!Number.isFinite(id)) return null
    return {
      id,
      client_name: typeof data.client_name === 'string' ? data.client_name : name,
    }
  }, [organisationId])

  const searchMembers = useCallback(async (query: string): Promise<QaParticipantPerson[]> => {
    // Reused from: packages/job-hub/src/jobs/qa/QaParticipantsEditor.tsx (search prop)
    const { data } = await api().get('/qa/participant-candidates/', { params: { q: query } })
    return rowsOf(data).map((row) => ({
      user_id: Number(row.user_id),
      name: typeof row.name === 'string' ? row.name : '',
      email: typeof row.email === 'string' ? row.email : '',
    })).filter((row) => Number.isFinite(row.user_id))
  }, [])

  const onAddParticipant = useCallback((person: QaParticipantPerson, role: string) => {
    setStaged((prev) => {
      const next = prev.filter((row) => row.key !== String(person.user_id))
      next.push({
        key: String(person.user_id),
        name: person.name,
        email: person.email,
        role,
      })
      return next
    })
  }, [])

  const onRemoveParticipant = useCallback((row: QaParticipantRow) => {
    setStaged((prev) => prev.filter((r) => r.key !== row.key))
  }, [])

  const createMutation = useMutation({
    mutationFn: async () => {
      const trimmed = title.trim()
      if (!trimmed) throw new Error('Job title is required')
      // Reused from: packages/job-hub/src/jobs-list/DraftJobCreateDialog.tsx (mutationFn gateway.create)
      const { data: job } = await api().post<CreatedJobPayload>(
        '/v2/jobs/',
        {
          display_name: trimmed,
          reporting_mode: 'draft',
          ...(clientId ? { client_id: clientId } : {}),
        },
        { params: { tenant: organisationId } },
      )
      if (!job?.id) throw new Error('Job created but no id returned')

      const followUps: Promise<unknown>[] = staged.map((row) =>
        api().post('/qa/participants/', {
          job: job.id,
          user: Number(row.key),
          role_on_job: row.role,
        }),
      )
      const reportTrimmed = reportTitle.trim()
      if (reportTrimmed) {
        followUps.push(
          api().put(`/report/job/${job.id}/hub/`, { itp_report_title: reportTrimmed }),
        )
      }
      const settled = await Promise.allSettled(followUps)
      const rejected = settled
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected')
        .map((r) => (r.reason instanceof Error ? r.reason.message : String(r.reason)))
      if (rejected.length > 0) {
        showToast({ type: 'error', message: rejected.join('; ') })
      }
      return job
    },
    onSuccess: (job) => {
      onCreated({
        job_id: job.id,
        client: job.client_name ?? null,
        site: job.site_address ?? null,
        status: job.status ?? 'tbc',
        tags: [],
        display_name: job.display_name ?? title.trim(),
      })
      onClose()
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Could not create the job')
    },
  })

  return {
    title,
    setTitle,
    titleReady,
    clientLabel,
    setClientLabel,
    setClientId,
    reportTitle,
    setReportTitle,
    staged,
    error,
    searchClients,
    createClient,
    searchMembers,
    onAddParticipant,
    onRemoveParticipant,
    createMutation,
    canCreate: titleReady && title.trim().length > 0 && !createMutation.isPending,
  }
}
